// Browser-side S3 multipart upload helper.
//
// Drives a 4-step flow against any of our /multipart/* route groups
// (footage, video-assets):
//   1. POST <start>      → { uploadId, partSize, ...idFields }
//   2. for each part:  POST <signPart> → { url }; PUT the blob; keep ETag
//   3. POST <complete>   → S3 assembles the object
//   4. on failure/cancel: POST <abort> → S3 discards parts + server cleans DB
//
// The caller still calls /confirm after a successful complete — that part of
// the flow is unchanged.

const DEFAULT_PART_SIZE   = 64 * 1024 * 1024; // 64 MB
const DEFAULT_CONCURRENCY = 4;                // parallel parts per file
const PART_MAX_ATTEMPTS   = 3;                // retries per individual part

export function multipartUpload({
  file,
  api,
  endpoints,          // { start, signPart, complete, abort }
  startBody  = {},    // extra fields posted to <start> (e.g. clientId)
  idFieldName,        // 'footageId' or 'videoAssetId' — the key in the start response
  partSize    = DEFAULT_PART_SIZE,
  concurrency = DEFAULT_CONCURRENCY,
  onProgress,
}) {
  const ac = new AbortController();
  const { signal } = ac;
  let idValue = null;
  let uploadId = null;

  const promise = (async () => {
    const startRes = await api.post(endpoints.start, {
      ...startBody,
      fileName: file.name,
      fileSize: file.size,
    });
    idValue  = startRes.data[idFieldName];
    uploadId = startRes.data.uploadId;
    const effectivePartSize = startRes.data.partSize || partSize;
    if (!idValue || !uploadId) throw new Error('multipart/start returned no id/uploadId');

    const partCount = Math.max(1, Math.ceil(file.size / effectivePartSize));
    const partProgress = new Array(partCount).fill(0);
    const finishedParts = [];

    const reportProgress = () => {
      if (!onProgress) return;
      const loaded = partProgress.reduce((sum, n) => sum + n, 0);
      onProgress(loaded, file.size);
    };

    async function uploadOnePart(partNumber) {
      const startByte = (partNumber - 1) * effectivePartSize;
      const endByte   = Math.min(startByte + effectivePartSize, file.size);
      const blob      = file.slice(startByte, endByte);

      const { data } = await api.post(endpoints.signPart, {
        [idFieldName]: idValue, uploadId, partNumber,
      });

      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            partProgress[partNumber - 1] = e.loaded;
            reportProgress();
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            const etag = xhr.getResponseHeader('ETag');
            if (!etag) return reject(new Error(`Part ${partNumber}: missing ETag in response`));
            partProgress[partNumber - 1] = blob.size;
            reportProgress();
            resolve({ PartNumber: partNumber, ETag: etag });
          } else {
            reject(new Error(`Part ${partNumber} failed: HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error(`Part ${partNumber}: network error`));
        xhr.onabort = () => reject(new Error('cancelled'));
        if (signal.aborted) { return reject(new Error('cancelled')); }
        signal.addEventListener('abort', () => xhr.abort(), { once: true });
        xhr.open('PUT', data.url);
        xhr.send(blob);
      });
    }

    async function uploadPartWithRetry(partNumber) {
      let lastErr;
      for (let attempt = 0; attempt < PART_MAX_ATTEMPTS; attempt++) {
        if (signal.aborted) throw new Error('cancelled');
        try {
          return await uploadOnePart(partNumber);
        } catch (err) {
          lastErr = err;
          if (err.message === 'cancelled') throw err;
          if (attempt < PART_MAX_ATTEMPTS - 1) {
            await new Promise(r => setTimeout(r, 500 * 2 ** attempt));
          }
        }
      }
      throw lastErr;
    }

    let nextPart = 1;
    async function worker() {
      while (true) {
        if (signal.aborted) throw new Error('cancelled');
        const partNumber = nextPart++;
        if (partNumber > partCount) return;
        const part = await uploadPartWithRetry(partNumber);
        finishedParts.push(part);
      }
    }

    try {
      const workerCount = Math.min(concurrency, partCount);
      await Promise.all(Array.from({ length: workerCount }, () => worker()));
      await api.post(endpoints.complete, {
        [idFieldName]: idValue, uploadId, parts: finishedParts,
      });
      return { [idFieldName]: idValue };
    } catch (err) {
      if (idValue && uploadId) {
        api.post(endpoints.abort, { [idFieldName]: idValue, uploadId })
          .catch(abortErr => console.error('[multipartUpload] abort failed:', abortErr.message));
      }
      throw err;
    }
  })();

  return {
    promise,
    cancel: () => ac.abort(),
  };
}
